import React from 'react'
import { Carousel } from 'react-responsive-carousel'
import styled from 'styled-components'

import type { SpecialZone } from '~/features/event-info/types/event-info'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'

import 'react-responsive-carousel/lib/styles/carousel.min.css'

type Props = {
  specialZoneList: SpecialZone[]
}

interface DotProps {
  selected: boolean
  onClick: () => void
}

function SpecialZone({ specialZoneList }: Props) {
  const viewport = useViewport()
  const isMobile = viewport <= theme.deviceSizes.medium
  const [selectedItem, setSelectedItem] = React.useState(0)

  const handleChange = (index: number) => {
    setSelectedItem(index)
  }

  return (
    <Wrapper>
      <Title>온리 특별존</Title>
      <CarouselWrapper>
        <StyledCarousel
          showThumbs={false}
          showArrows={false}
          showStatus={false}
          showIndicators={false}
          infiniteLoop={true}
          emulateTouch={true}
          dynamicHeight={false}
          autoPlay={true}
          interval={3000}
          selectedItem={selectedItem}
          onChange={handleChange}
          centerMode={true}
          centerSlidePercentage={isMobile ? 100 : 33.33}
        >
          {specialZoneList.map((zone, index) => (
            <CarouselItem
              key={zone.id}
              className={`carousel-item-${index % 3}`}
            >
              <Image src={zone.image_info.url} alt={zone.zone_name} />
              <ItemTitle>{zone.zone_name}</ItemTitle>
              <Description>{zone.zone_desc}</Description>
            </CarouselItem>
          ))}
        </StyledCarousel>
        <Indicators>
          {specialZoneList.map((_, index) => (
            <Dot
              key={index}
              selected={index === selectedItem}
              onClick={() => handleChange(index)}
            />
          ))}
        </Indicators>
      </CarouselWrapper>
    </Wrapper>
  )
}

export default SpecialZone

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;

  height: auto;
  padding: 36px 24px;

  background-color: white;
  border: 1px solid #c1c3c2;
  border-radius: 12px;
`

const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  font-weight: 500;
`

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledCarousel = styled(Carousel)`
  height: 100%;

  .carousel .slide {
    display: flex;
    justify-content: center;
    height: 100%;
  }

  .carousel .slide img {
    border-radius: 24px;
  }

  .carousel .carousel-slider {
    overflow: visible;
    height: 100%;
  }
`

const CarouselItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 330px;
  height: 380px;
  margin-right: 20px;
  padding: 20px;

  background-color: white;
  border-radius: 24px;

  &.carousel-item-0 {
    border: 2px solid #8bd2d7;
  }

  &.carousel-item-1 {
    border: 2px solid #f6d9e3;
  }

  &.carousel-item-2 {
    border: 2px solid #fde1a5;
  }

  @media ${theme.device.medium} {
    width: 100%;
    margin-right: 0;
  }
`

const Image = styled.img`
  width: 290px;
  height: 154px;
  border-radius: 24px;
`

const ItemTitle = styled.h3`
  margin: 20px 0;
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 24px;
  font-weight: 500;
`

const Description = styled.p`
  margin: 20px 0;
  font-size: 16px;
  text-align: center;
  white-space: pre-line;
`

const Indicators = styled.div`
  position: absolute;
  bottom: -20px;

  display: flex;
  justify-content: center;

  width: 100%;
`

const Dot = styled.div<DotProps>`
  cursor: pointer;

  width: 10px;
  height: 10px;
  margin: 0 5px;

  background: ${(props) => (props.selected ? '#8bd2d7' : '#eaebeb')};
  border-radius: 50%;
`
